import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../component/Header';
import './FAQ.css';

const FAQ = () => {
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedCategory, setSelectedCategory] = useState('all');
	const [expandedItems, setExpandedItems] = useState(new Set());
	const navigate = useNavigate();

	const categories = [
		{ id: 'all', label: 'All Questions', icon: '❓', count: 0 },
		{ id: 'general', label: 'General', icon: '❓', count: 0 },
		{ id: 'security', label: 'Security', icon: '🔒', count: 0 },
		{ id: 'document', label: 'Document Translation', icon: '📄', count: 0 },
	];

	const faqData = [
		// General Questions
		{
			id: 1,
			question: 'What is Letz Chat Translation?',
			answer:
				'Letz Chat Translation is a comprehensive AI-powered translation platform that offers real-time text, document, voice, video, and website translation services. We use advanced Large Language Models (LLMs) to provide context-aware, high-quality translations in over 100 languages.',
			category: 'general',
		},
		{
			id: 2,
			question: 'How do I get started with Letz Chat Translation?',
			answer:
				'Getting started is easy! Simply create an account, choose your subscription plan, and you can immediately begin translating text, uploading documents, or using our voice and video translation features. No setup or installation required.',
			category: 'general',
		},
		{
			id: 3,
			question: 'What languages are supported?',
			answer:
				'We support over 100 languages including English, Spanish, French, German, Chinese, Japanese, Arabic, Hindi, Portuguese, Russian, and many more. Our AI models are continuously updated to support new languages and dialects.',
			category: 'general',
		},
		{
			id: 4,
			question: 'How accurate are the translations?',
			answer:
				'Our translations are highly accurate thanks to our advanced LLM technology. We achieve 95%+ accuracy for most language pairs, with context-aware translations that understand nuance, tone, and cultural context. For specialized content, we recommend using our glossary feature.',
			category: 'general',
		},
		{
			id: 5,
			question: 'Can I use Letz Chat Translation for business purposes?',
			answer:
				'Absolutely! Letz Chat Translation is designed for both personal and business use. We offer enterprise features, API access, team collaboration tools, and custom solutions for businesses of all sizes.',
			category: 'general',
		},
		{
			id: 30,
			question: 'How do I check my translation history?',
			answer:
				'You can access your translation history by clicking on your profile icon in the top right corner and selecting "Document Translation History" or "Voice Dubbing History" from the dropdown menu. This will show you all your past translations, their status, and allow you to download completed translations.',
			category: 'general',
		},

		// Security
		{
			id: 11,
			question: 'Is my data secure?',
			answer:
				'Yes, security is our top priority. We use end-to-end encryption, comply with GDPR and CCPA regulations, and never store your translated content permanently. All data is processed securely and deleted after translation completion.',
			category: 'security',
		},
		{
			id: 12,
			question: 'Do you store my documents?',
			answer:
				'No, we do not permanently store your documents. Files are processed securely and automatically deleted after translation. We only retain metadata for billing and service improvement purposes, never the actual content.',
			category: 'security',
		},
		{
			id: 13,
			question: 'Can I use Letz Chat for confidential business documents?',
			answer:
				'Yes, our platform is designed for confidential business use. We offer additional security features for enterprise customers, including on-premises deployment options and custom security protocols.',
			category: 'security',
		},

		// Document Translation
		{
			id: 17,
			question: 'How long does document translation take?',
			answer:
				'Translation time varies based on document size and complexity. Small documents (1-5 pages) typically take 2-5 minutes, while larger documents (20+ pages) may take 30 minutes to several hours. We use LLM technology for context-aware translation.',
			category: 'document',
		},
		{
			id: 18,
			question: 'Can I track the progress of my document translation?',
			answer:
				'Yes, you can monitor translation progress in real-time. We provide a progress bar and estimated completion time. You can also check your translation history to see the status of all your documents.',
			category: 'document',
		},
		{
			id: 19,
			question: 'What if my document translation fails?',
			answer:
				"If translation fails, we'll automatically retry the process. If it continues to fail, our support team will investigate and provide a solution. You won't be charged for failed translations.",
			category: 'document',
		},
		{
			id: 29,
			question: 'What should I do if my translation fails?',
			answer:
				"If your translation fails, please first check your translation history to see if the translation was completed there. If the translation also failed in your history, then contact our admin support team. We'll investigate the issue and provide a solution. You won't be charged for failed translations.",
			category: 'general',
		},

		// Troubleshooting
		{
			id: 31,
			question: 'My translation is stuck or taking too long, what should I do?',
			answer:
				'If your translation appears stuck or is taking longer than expected, first check your translation history to see if it completed there. If it shows as failed in your history, contact our admin support team. For large documents, processing can take several hours, so please be patient.',
			category: 'general',
		},
		{
			id: 32,
			question:
				'I received an error message during translation, what does it mean?',
			answer:
				'Error messages usually indicate a temporary issue with file processing or server capacity. First, check your translation history to see if the translation completed despite the error. If it failed, try uploading the file again. If the problem persists, contact our admin support team with the error details.',
			category: 'general',
		},
	];

	// Calculate category counts
	const categoryCounts = useMemo(() => {
		const counts = {};
		faqData.forEach((item) => {
			counts[item.category] = (counts[item.category] || 0) + 1;
		});
		counts.all = faqData.length;
		return counts;
	}, []);

	// Filter FAQ data based on search and category
	const filteredFAQs = useMemo(() => {
		return faqData.filter((faq) => {
			const matchesSearch =
				faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
				faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
			const matchesCategory =
				selectedCategory === 'all' || faq.category === selectedCategory;
			return matchesSearch && matchesCategory;
		});
	}, [searchTerm, selectedCategory]);

	const handleCategoryClick = (categoryId) => {
		setSelectedCategory(categoryId);
	};

	const handleBackToTranslation = () => {
		navigate('/');
	};

	const toggleExpanded = (itemId) => {
		const newExpanded = new Set(expandedItems);
		if (newExpanded.has(itemId)) {
			newExpanded.delete(itemId);
		} else {
			newExpanded.add(itemId);
		}
		setExpandedItems(newExpanded);
	};

	return (
		<>
			<Header />
			<div className="faq-container">
				<div className="faq-content">
					{/* Header */}
					<div className="faq-header">
						<h1 className="faq-title">Frequently Asked Questions</h1>
						<p className="faq-subtitle">
							Find answers to common questions about Letz Chat Translation
							services
						</p>
					</div>

					{/* Search and Filter */}
					<div className="faq-search-section">
						<div className="search-container">
							<input
								type="text"
								placeholder="Search questions..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="search-input"
							/>
							<span className="search-icon">🔍</span>
						</div>

						<div className="category-filters">
							{categories.map((category) => (
								<button
									key={category.id}
									onClick={() => handleCategoryClick(category.id)}
									className={`category-chip ${
										selectedCategory === category.id ? 'active' : ''
									}`}
								>
									<span className="category-icon">{category.icon}</span>
									<span className="category-label">{category.label}</span>
									<span className="category-count">
										({categoryCounts[category.id] || 0})
									</span>
								</button>
							))}
						</div>
					</div>

					{/* FAQ List */}
					<div className="faq-list">
						{filteredFAQs.length > 0 ? (
							filteredFAQs.map((faq) => (
								<div key={faq.id} className="faq-item">
									<div
										className="faq-question"
										onClick={() => toggleExpanded(faq.id)}
									>
										<h3>{faq.question}</h3>
										<span
											className={`expand-icon ${
												expandedItems.has(faq.id) ? 'expanded' : ''
											}`}
										>
											{expandedItems.has(faq.id) ? '−' : '+'}
										</span>
									</div>
									{expandedItems.has(faq.id) && (
										<div className="faq-answer">
											<p>{faq.answer}</p>
										</div>
									)}
								</div>
							))
						) : (
							<div className="no-results">
								<p>No questions found matching your search criteria.</p>
							</div>
						)}
					</div>

					{/* Back to Translation Button */}
					<div className="faq-footer">
						<button onClick={handleBackToTranslation} className="back-button">
							← Back to Translation
						</button>
					</div>
				</div>
			</div>
		</>
	);
};

export default FAQ;
